import React, { useEffect, useState, useRef } from 'react';
import { QuestionRendererProps } from './types';
import { motion } from 'framer-motion';
import { CheckCircle2, XCircle } from 'lucide-react';
import { VerbConjugationFlowConfig, VerbFlowStep } from '../../../content/types';
import { Input } from '../../../components/ui/Input';
export function VerbConjugationFlowRenderer({
  question,
  onAnswer,
  onReadyChange,
  shouldValidate
}: QuestionRendererProps) {
  const config = (question.data?.verbConjugationFlow || question.data || {}) as VerbConjugationFlowConfig;
  const steps: VerbFlowStep[] = config.steps || [];
  const context = config.context || '';
  const [userSteps, setUserSteps] = useState<Record<number, string>>({});
  const prevValidateRef = useRef(false);
  useEffect(() => {
    setUserSteps({});
    prevValidateRef.current = false;
  }, [question.id]);
  useEffect(() => {
    if (!shouldValidate) prevValidateRef.current = false;
  }, [shouldValidate]);
  useEffect(() => {
    const allFilled = steps.every((_, i) => userSteps[i] && userSteps[i].trim() !== '');
    onReadyChange?.(allFilled);
  }, [userSteps, steps.length, onReadyChange]);
  useEffect(() => {
    if (shouldValidate && !prevValidateRef.current) {
      prevValidateRef.current = true;
      onAnswer(JSON.stringify(userSteps));
    }
  }, [shouldValidate, userSteps, onAnswer]);
  const handleInputChange = (index: number, value: string) => {
    if (shouldValidate) return;
    setUserSteps(prev => ({
      ...prev,
      [index]: value
    }));
  };
  const handleChoiceSelect = (index: number, value: string) => {
    if (shouldValidate) return;
    setUserSteps(prev => ({
      ...prev,
      [index]: value
    }));
  };
  const isStepCorrect = (index: number) => {
    const step = steps[index];
    const userVal = userSteps[index]?.trim() || '';
    if (step.type === 'choice') {
      const correctOption = step.options?.find(opt => opt.isCorrect);
      return correctOption?.value === userVal;
    } else {
      const accepted = step.acceptedAnswers || [];
      return accepted.some(ans => String(ans).trim().toLowerCase() === userVal.toLowerCase());
    }
  };
  return <div className="w-full max-w-xl mx-auto flex flex-col gap-4" data-id="element-7618">
      {context && <div className="px-3 py-2 bg-indigo-50 text-indigo-800 rounded-lg border border-indigo-100 text-sm font-medium" data-id="element-7619">
          {context}
        </div>}

      <div className="flex flex-col gap-3" data-id="element-7620">
        {steps.map((step, index) => {
        let borderClass = 'border-slate-200 bg-white';
        let ValidationIcon = null;
        if (shouldValidate) {
          const correct = isStepCorrect(index);
          borderClass = correct ? 'border-emerald-300 bg-emerald-50' : 'border-red-300 bg-red-50';
          ValidationIcon = correct ? <CheckCircle2 className="w-4 h-4 text-emerald-500 shrink-0" data-id="element-7621" /> : <XCircle className="w-4 h-4 text-red-500 shrink-0" data-id="element-7622" />;
        }
        return <motion.div key={index} initial={{
          opacity: 0,
          y: 8
        }} animate={{
          opacity: 1,
          y: 0
        }} transition={{
          delay: index * 0.05
        }} className={`p-3 rounded-xl border ${borderClass}`} data-id="element-7623">
              <div className="flex items-center justify-between gap-2 mb-2" data-id="element-7624">
                <div className="flex items-center gap-2" data-id="element-7625">
                  <span className="w-6 h-6 rounded-full bg-slate-100 text-slate-500 flex items-center justify-center font-bold text-xs shrink-0" data-id="element-7626">
                    {index + 1}
                  </span>
                  <span className="font-medium text-slate-700 text-sm" data-id="element-7627">
                    {step.prompt}
                  </span>
                </div>
                {ValidationIcon}
              </div>

              <div className="pl-8" data-id="element-7628">
                {step.type === 'input' ? <Input value={userSteps[index] || ''} onChange={e => handleInputChange(index, e.target.value)} disabled={shouldValidate} placeholder="Typ je antwoord..." className="text-sm" data-id="element-7629" /> : <div className={`flex gap-2 flex-wrap`} data-id="element-7630">
                    {step.options?.map((opt, optIdx) => {
                const isSelected = userSteps[index] === opt.value;
                let btnClass = 'border border-slate-200 bg-white text-slate-700 hover:border-indigo-300 hover:bg-indigo-50';
                if (isSelected && !shouldValidate) {
                  btnClass = 'border border-indigo-500 bg-indigo-50 text-indigo-700 ring-1 ring-indigo-200';
                } else if (shouldValidate) {
                  if (opt.isCorrect) {
                    btnClass = 'border border-emerald-500 bg-emerald-50 text-emerald-700';
                  } else if (isSelected && !opt.isCorrect) {
                    btnClass = 'border border-red-500 bg-red-50 text-red-700';
                  } else {
                    btnClass = 'border border-slate-100 bg-slate-50 text-slate-400 opacity-50';
                  }
                }
                return <button key={optIdx} onClick={() => handleChoiceSelect(index, opt.value)} disabled={shouldValidate} className={`px-3 py-2 rounded-lg text-sm font-medium transition-all ${btnClass}`} data-id="element-7631">
                          {opt.label}
                        </button>;
              })}
                  </div>}

                {shouldValidate && step.type === 'choice' && !isStepCorrect(index) && <p className="mt-2 text-xs text-red-600" data-id="element-7632">
                      {step.options?.find(o => o.value === userSteps[index])?.feedback || 'Dat is niet juist.'}
                    </p>}
              </div>
            </motion.div>;
      })}
      </div>
    </div>;
}